import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../context/NotificationContext';

const datePresets = [
  { label: '7 дней', value: '7' },
  { label: '14 дней', value: '14' },
  { label: '30 дней', value: '30' },
  { label: '90 дней', value: '90' },
  { label: 'Произвольный', value: 'custom' },
];

const strategyTypes = [
  { value: 'target_cpc', label: 'По стоимости клика' },
  { value: 'conversion_cost', label: 'По стоимости конверсии' },
  { value: 'threshold_scout', label: 'Скаут порога' },
  { value: 'drr', label: 'По ДРР' },
];

const formatDateYmd = (date) => {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const prepareStatsFromRows = (rows = []) => {
  const daily = rows.map((row) => {
    const impressions = Number(row.impressions ?? row.views) || 0;
    const clicks = Number(row.clicks) || 0;
    const spend = Number(row.cost ?? row.spend) || 0;
    const orders = Number(row.orders) || 0;
    const revenue = Number(row.revenue) || 0;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    const cpc = clicks > 0 ? spend / clicks : 0;
    const acos = revenue > 0 ? (spend / revenue) * 100 : null;
    return {
      date: row.date,
      views: impressions,
      clicks,
      spend,
      orders,
      revenue,
      ctr,
      cpc,
      acos,
    };
  });

  const totals = daily.reduce(
    (acc, row) => {
      acc.views += row.views;
      acc.clicks += row.clicks;
      acc.spend += row.spend;
      acc.orders += row.orders;
      acc.revenue += row.revenue;
      return acc;
    },
    { views: 0, clicks: 0, spend: 0, orders: 0, revenue: 0 }
  );

  const ctr = totals.views > 0 ? (totals.clicks / totals.views) * 100 : 0;
  const cpc = totals.clicks > 0 ? totals.spend / totals.clicks : 0;
  const acos = totals.revenue > 0 ? (totals.spend / totals.revenue) * 100 : null;

  return { totals: { ...totals, ctr, cpc, acos }, daily };
};

export default function CampaignDetail({ routePrefix = '' }) {
  const { notify } = useNotification();
  const { clientId, advertId } = useParams();
  const navigate = useNavigate();
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [bidderEnabled, setBidderEnabled] = useState(false);
  const [strategyType, setStrategyType] = useState('target_cpc');
  const [targetCpc, setTargetCpc] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [targets, setTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  useEffect(() => {
    fetchCampaignInfo();
  }, [advertId]);

  useEffect(() => {
    if (campaignInfo) {
      fetchStats();
    }
  }, [campaignInfo, dateRange, customFrom, customTo]);

  useEffect(() => {
    if (campaignInfo?.id) {
      fetchBidTargets(campaignInfo.id);
    }
  }, [campaignInfo?.id]);

  const getDateRange = () => {
    if (dateRange === 'custom') {
      if (!customFrom || !customTo) {
        return { from: null, to: null };
      }
      return { from: customFrom, to: customTo };
    }

    const days = Number(dateRange);
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days + 1);
    return { from: formatDateYmd(from), to: formatDateYmd(to) };
  };

  const fetchCampaignInfo = async () => {
    setLoadingInfo(true);
    setError('');
    try {
      const { data } = await apiClient.get('/stats/campaigns/user');
      const found = data.campaigns.find((c) => c.advert_id === Number(advertId));
      if (found) {
        applyCampaignInfo(found);
        return;
      }
      throw new Error('Кампания не найдена');
    } catch (err) {
      if (clientId) {
        try {
          const fallback = await apiClient.get(`/stats/campaigns?client_id=${clientId}`);
          const found = fallback.data.campaigns.find((c) => c.advert_id === Number(advertId));
          if (found) {
            applyCampaignInfo({ ...found, client_id: Number(clientId) });
            return;
          }
        } catch (fallbackErr) {
          console.error('Ошибка fallback campaignInfo:', fallbackErr.message);
        }
      }
      console.error('Ошибка получения информации о кампании:', err.message);
      setError('Кампания не найдена или недоступна');
    } finally {
      setLoadingInfo(false);
    }
  };

  const applyCampaignInfo = (info) => {
    setCampaignInfo(info);
    setBidderEnabled(Boolean(info.bidder_enabled));
    setStrategyType(info.strategy_type || 'target_cpc');
  };

  const fetchStats = async () => {
    if (!campaignInfo) return;
    const range = getDateRange();
    if (!range.from || !range.to) {
      return;
    }

    setStatsLoading(true);
    setError('');

    try {
      const { data } = await apiClient.get('/stats/campaign/user', {
        params: {
          campaign_id: campaignInfo.id,
          from: range.from,
          to: range.to,
        },
      });

      const prepared = prepareStatsFromRows(data.data || []);
      setStats(prepared);
      fetchProducts(campaignInfo.client_id || Number(clientId), range);
    } catch (err) {
      console.warn('Fallback статистики:', err.message);
      const clientParam = campaignInfo.client_id || Number(clientId);
      if (!clientParam) {
        setError('Не удалось загрузить статистику кампании');
        setStats(null);
        setProducts([]);
        setStatsLoading(false);
        return;
      }

      try {
        const fallback = await apiClient.get('/stats/campaign', {
          params: {
            client_id: clientParam,
            advert_id: campaignInfo.advert_id,
            from: range.from,
            to: range.to,
          },
        });
        setStats(fallback.data);
        await fetchProducts(clientParam, range);
      } catch (fallbackErr) {
        console.error('Ошибка fallback статистики:', fallbackErr.message);
        setError('Не удалось загрузить статистику кампании');
        setStats(null);
        setProducts([]);
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchProducts = async (clientParam, range) => {
    if (!clientParam || !campaignInfo) {
      setProducts([]);
      return;
    }

    try {
      const { data } = await apiClient.get('/stats/campaign/products', {
        params: {
          client_id: clientParam,
          advert_id: campaignInfo.advert_id,
          from: range.from,
          to: range.to,
        },
      });
      setProducts(data.products || []);
    } catch (err) {
      console.warn('Не удалось загрузить товары:', err.message);
      setProducts([]);
    }
  };

  const fetchBidTargets = async (campaignId) => {
    if (!campaignId) return;
    setTargetsLoading(true);
    try {
      const { data } = await apiClient.get(`/bidder/targets/${campaignId}`);
      setTargets(Array.isArray(data.targets) ? data.targets : []);
    } catch (err) {
      console.warn('Не удалось загрузить ставки биддера:', err.message);
      setTargets([]);
    } finally {
      setTargetsLoading(false);
    }
  };

  const handleSaveBidderSettings = async () => {
    if (!campaignInfo) return;
    try {
      console.log('Сохранение настроек:', {
        campaign_id: campaignInfo.id,
        bidder_enabled: bidderEnabled,
        strategy_type: strategyType,
        target_cpc: targetCpc,
      });
      notify('Настройки биддера сохранены (заглушка)', 'success');
    } catch (err) {
      notify(`Ошибка сохранения: ${err.message}`, 'error');
    }
  };

  if (loadingInfo) return <div className="p-4">Загрузка кампании...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!campaignInfo) return <div className="p-4">Кампания не найдена</div>;

  const totals = stats?.totals || { views: 0, clicks: 0, spend: 0, orders: 0, revenue: 0, ctr: 0, cpc: 0, acos: null };
  const sortedTargets = [...targets]
    .sort((a, b) => (b.current_bid || 0) - (a.current_bid || 0))
    .slice(0, 8);

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`${routePrefix}/client/${clientId}`)}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Назад к интерфейсу клиента
            </button>
            <div>
              <h1 className="text-2xl font-bold">
                {campaignInfo.advert_id} — {campaignInfo.name}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    bidderEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {bidderEnabled ? 'Оптимизация применяется' : 'Оптимизация не применяется'}
                </span>
                <span className="text-xs text-gray-500">
                  Статус: {campaignInfo.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Период анализа:</label>
          <div className="flex gap-2">
            {datePresets.map((preset) => (
              <button
                key={preset.value}
                onClick={() => setDateRange(preset.value)}
                className={`px-3 py-1 rounded text-sm ${
                  dateRange === preset.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {dateRange === 'custom' && (
            <div className="flex gap-2 mt-2">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="border rounded px-2 py-1"
                placeholder="От"
              />
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="border rounded px-2 py-1"
                placeholder="До"
              />
            </div>
          )}
        </div>

        {statsLoading ? (
          <div className="p-4">Загрузка статистики...</div>
        ) : (
          stats && (
            <div className="bg-white border rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Статистика кампании</h2>
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{totals.spend.toLocaleString()} ₽</div>
                  <div className="text-sm text-gray-600">Расходы</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{totals.views.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Показы</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{totals.clicks.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Клики</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{totals.orders}</div>
                  <div className="text-sm text-gray-600">Конверсии</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-6 mt-6">
                <div className="text-center">
                  <div className="text-lg font-semibold">{totals.ctr.toFixed(2)}%</div>
                  <div className="text-sm text-gray-600">CTR</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{totals.cpc.toFixed(2)} ₽</div>
                  <div className="text-sm text-gray-600">Ср. цена за клик</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{totals.acos ? `${totals.acos.toFixed(2)}%` : '—'}</div>
                  <div className="text-sm text-gray-600">ACoS</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">
                    {totals.orders > 0 ? (totals.spend / totals.orders).toFixed(2) : '—'} ₽
                  </div>
                  <div className="text-sm text-gray-600">Ср. цена за конверсию</div>
                </div>
              </div>
            </div>
          )
        )}

        {sortedTargets.length > 0 || targetsLoading ? (
          <div className="bg-white border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Текущие ставки</h2>
              {targetsLoading && <span className="text-xs text-gray-500">обновляется…</span>}
            </div>
            {targetsLoading ? (
              <div className="text-sm text-gray-500">Загружаем ставки…</div>
            ) : sortedTargets.length === 0 ? (
              <div className="text-sm text-gray-500">Ставки пока не доступны</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase text-gray-500 border-b">
                    <tr>
                      <th className="py-2">Тип</th>
                      <th className="py-2">Ключевое слово / Артикул</th>
                      <th className="py-2">Текущая ставка</th>
                      <th className="py-2">CPO</th>
                      <th className="py-2">DRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTargets.map((target) => (
                      <tr key={`${target.id}-${target.keyword || target.nm_id}`} className="border-b">
                        <td className="py-2 text-xs uppercase text-gray-600">{target.target_type}</td>
                        <td className="py-2">
                          {target.keyword || `nm:${target.nm_id}`}
                          <br />
                          <span className="text-xs text-gray-400">campaign #{target.campaign_id}</span>
                        </td>
                        <td className="py-2">
                          {target.current_bid ? `${Number(target.current_bid).toFixed(2)} ₽` : '—'}
                        </td>
                        <td className="py-2">
                          {target.cpo !== null && target.cpo !== undefined
                            ? `${Number(target.cpo).toFixed(2)} ₽`
                            : '—'}
                        </td>
                        <td className="py-2">
                          {target.drr !== null && target.drr !== undefined
                            ? `${Number(target.drr).toFixed(2)}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {products.length > 0 && (
          <div className="bg-white border rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Топ товары по расходам</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Товар</th>
                    <th className="text-right py-2">Расходы</th>
                    <th className="text-right py-2">Клики</th>
                    <th className="text-right py-2">CTR</th>
                    <th className="text-right py-2">CPC</th>
                    <th className="text-right py-2">Заказы</th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, 10).map((product) => (
                    <tr key={product.nm_id} className="border-b">
                      <td className="py-2">
                        <div className="text-sm font-medium">{product.product_name}</div>
                        <div className="text-xs text-gray-500">ID: {product.nm_id}</div>
                      </td>
                      <td className="text-right py-2">{product.spend.toLocaleString()} ₽</td>
                      <td className="text-right py-2">{product.clicks}</td>
                      <td className="text-right py-2">{product.ctr.toFixed(2)}%</td>
                      <td className="text-right py-2">{product.cpc.toFixed(2)} ₽</td>
                      <td className="text-right py-2">{product.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="w-80 bg-gray-50 border-l p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">Настроить оптимизацию</h2>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Тип стратегии:</label>
            <select
              value={strategyType}
              onChange={(e) => setStrategyType(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {strategyTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {strategyType === 'conversion_cost' && (
            <div>
              <label className="block text-sm font-medium mb-2">Цена конверсии:</label>
              <input
                type="number"
                value={targetCpc}
                onChange={(e) => setTargetCpc(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="Введите целевую цену конверсии"
              />
              <p className="text-xs text-gray-500 mt-1">
                Для удержания укажите цену конверсии текущую или ниже. Для увеличения используйте стратегию «Масштабирование».
              </p>
            </div>
          )}

          <button
            onClick={handleSaveBidderSettings}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded"
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  );
}
